import { useRef } from "react";
import { Link } from "react-router-dom";
import { Pencil, Trash2, ArrowLeftRight, ClipboardList, FileSearch, Plus, Camera, Gauge, Fuel, Cog, CarFront, TrendingUp, ShieldCheck, Wrench, BadgeCheck } from "lucide-react";
import { photoUrl } from "../api";
import { EVENT_META, fmtDate, fmtKm, fmtTry, scoreColor, SEVERITY_LABELS, vehicleDeleteRemainingMs, fmtHourMin, belgeTarih, belgeDurumu, fmtDateShort, editTimeLeft } from "../lib/labels";
import { autoVehicleImage, hasOwnPhoto } from "../lib/vehicleImages";

const BELGE_BOXES = [
  { key: "kasko", label: "Kasko" },
  { key: "sigorta", label: "Trafik Sigortası" },
  { key: "muayene", label: "Muayene" },
];

const daysLeft = (iso) => {
  if (!iso) return null;
  return Math.ceil((new Date(`${iso}T00:00:00`) - new Date(new Date().toDateString())) / 86400000);
};

export default function VehicleCard({ v, records, expanded, onToggle, onEdit, onTransfer, onDelete, onAddRecord, onEditRecord, onDeleteRecord, onPhotoChange }) {
  const delRemain = vehicleDeleteRemainingMs(v.created_at);
  const canDelete = delRemain === null || delRemain > 0;
  const photoRef = useRef(null);
  const own = hasOwnPhoto(v);
  const img = own ? photoUrl(v.photo || v.photos[0]) : autoVehicleImage(v.brand, v.model);
  const belgeler = belgeTarih(v.belgeler);

  const hist = v.history || [];
  const byTypes = (types) => hist.filter((h) => types.includes(h.type)).sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  const sonServis = byTypes(["servis"])[0];
  const sonEkspertiz = byTypes(["ekspertiz"])[0];
  const tramer = byTypes(["kaza", "hasar"]);
  const tramerCost = tramer.reduce((s, r) => s + (Number(r.cost) || 0), 0);
  const kmVals = hist.filter((h) => h.km != null).map((h) => Number(h.km));
  const sonKm = kmVals.length ? Math.max(...kmVals) : null;

  const upcoming = BELGE_BOXES
    .map((b) => ({ ...b, bitis: belgeler[b.key].bitis, days: daysLeft(belgeler[b.key].bitis) }))
    .filter((x) => x.days !== null && x.days <= 30)
    .sort((a, b) => a.days - b.days);

  const pickPhoto = (e) => {
    const f = e.target.files && e.target.files[0];
    e.target.value = "";
    if (f && onPhotoChange) onPhotoChange(v, f);
  };

  const specs = [
    { Icon: Gauge, label: "Kilometre", value: fmtKm(v.km), testid: "km" },
    { Icon: Fuel, label: "Yakıt", value: v.fuel || "—", testid: "fuel" },
    { Icon: Cog, label: "Vites", value: v.transmission || "—", testid: "transmission" },
    { Icon: CarFront, label: "Kasa Tipi", value: v.body_type || "—", testid: "body-type" },
    { Icon: CarFront, label: "Araç Türü", value: v.vehicle_type || "Otomobil", testid: "vehicle-type" },
    { Icon: Gauge, label: "Son Doğrulanan KM", value: sonKm != null ? fmtKm(sonKm) : "—", testid: "verified-km" },
  ];

  const summaries = [
    { Icon: ShieldCheck, label: "Tramer Özeti", testid: "tramer",
      value: tramer.length === 0 ? "Hasar kaydı yok" : `${tramer.length} kayıt${tramerCost ? ` • ${fmtTry(tramerCost)}` : ""}`,
      tone: tramer.length === 0 ? "text-emerald-300" : "text-amber-300" },
    { Icon: BadgeCheck, label: "Son Ekspertiz", testid: "ekspertiz",
      value: sonEkspertiz ? fmtDate(sonEkspertiz.date) : "Kayıt yok", tone: sonEkspertiz ? "text-slate-100" : "text-slate-500" },
    { Icon: Wrench, label: "Son Servis", testid: "servis",
      value: sonServis ? fmtDate(sonServis.date) : "Kayıt yok", tone: sonServis ? "text-slate-100" : "text-slate-500" },
  ];

  return (
    <div data-testid={`vehicle-card-${v.vin}`}
      className="rounded-2xl border border-white/10 bg-slate-900/60 backdrop-blur-xl overflow-hidden">
      <div className="p-5 sm:p-6 flex flex-col sm:flex-row gap-5">
        <div className="shrink-0">
          <input ref={photoRef} type="file" accept="image/jpeg,image/png" className="hidden"
            data-testid={`vehicle-photo-input-${v.vin}`} onChange={pickPhoto} />
          <button type="button" data-testid={`vehicle-photo-btn-${v.vin}`} onClick={() => photoRef.current?.click()}
            className="relative block w-full sm:w-56 h-40 rounded-xl overflow-hidden border border-white/15 bg-slate-950/60 group text-left">
            <img src={img} alt={`${v.brand} ${v.model}`} className="w-full h-full object-cover" />
            {!own && (
              <span data-testid={`vehicle-stock-badge-${v.vin}`}
                className="absolute top-1.5 left-1.5 text-[11px] font-bold px-2 py-0.5 rounded-md bg-slate-950/85 border border-white/10 text-slate-300">
                Örnek Görsel
              </span>
            )}
            <span className="absolute bottom-1.5 right-1.5 text-[11px] font-bold px-2.5 py-1 rounded-lg bg-slate-950/85 border border-white/10 text-slate-200 group-hover:text-cyan-300 group-hover:border-cyan-400/40 transition-colors">
              <Camera className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />{own ? "Fotoğrafı Değiştir" : "Fotoğraf Ekle"}
            </span>
          </button>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h3 className="text-xl sm:text-2xl font-extrabold text-white">
              {v.brand} {v.model} <span className="text-slate-400 font-semibold text-lg">{v.year}</span>
            </h3>
            {v.trim && <span data-testid={`vehicle-trim-${v.vin}`} className="text-xs font-bold px-2.5 py-1 rounded-full border border-white/15 text-slate-200 bg-white/5">{v.trim}</span>}
            <span className="text-sm font-bold px-3 py-1 rounded-full border" style={{ color: scoreColor(v.score), borderColor: `${scoreColor(v.score)}55`, backgroundColor: `${scoreColor(v.score)}14` }}>
              Skor: {v.score}
            </span>
          </div>
          <p className="mt-1.5 font-mono-vin text-sm text-cyan-300 tracking-widest">{v.vin}{v.plate ? ` • ${v.plate}` : ""}</p>

          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2.5">
            {specs.map((s) => (
              <div key={s.testid} data-testid={`card-spec-${s.testid}-${v.vin}`}
                className="rounded-lg border border-white/5 bg-slate-950/40 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  <s.Icon className="w-3 h-3" /> {s.label}
                </p>
                <p className="mt-0.5 text-sm font-bold text-slate-100">{s.value}</p>
              </div>
            ))}
            <div data-testid={`card-piyasa-${v.vin}`} className="rounded-lg border border-white/5 bg-slate-950/40 px-3 py-2">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Güncel Piyasa Değeri
              </p>
              <p className="mt-0.5 text-sm font-bold text-amber-300/90">Canlı veri bekleniyor</p>
              <Link to="/degerleme" data-testid={`card-degerleme-link-${v.vin}`} className="text-[11px] text-cyan-300 hover:text-cyan-200">Değerleme sayfası →</Link>
            </div>
          </div>

          <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            {summaries.map((s) => (
              <div key={s.testid} data-testid={`card-${s.testid}-${v.vin}`}
                className="rounded-lg border border-white/5 bg-slate-950/40 px-3 py-2">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1">
                  <s.Icon className="w-3 h-3" /> {s.label}
                </p>
                <p className={`mt-0.5 text-sm font-bold ${s.tone}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {upcoming.length > 0 && (
            <div data-testid={`card-upcoming-${v.vin}`} className="mt-3 flex flex-wrap gap-2">
              {upcoming.map((u) => (
                <span key={u.key} data-testid={`upcoming-chip-${u.key}-${v.vin}`}
                  className={`text-xs font-bold px-2.5 py-1 rounded-full border ${u.days < 0 ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-amber-500/40 bg-amber-500/10 text-amber-300"}`}>
                  {u.label}: {u.days < 0 ? "süresi doldu" : u.days === 0 ? "son gün" : `${u.days} gün kaldı`}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex sm:flex-col gap-2 shrink-0">
          <Link data-testid={`detail-vehicle-${v.vin}`} to={`/araclarim/${v.id}`}
            className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-400 text-slate-950 text-sm font-bold hover:bg-cyan-300 transition-colors">
            <ClipboardList className="w-4 h-4" /> Detay
          </Link>
          <Link data-testid={`view-report-${v.vin}`} to={`/rapor/${v.vin}`}
            className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-400/10 border border-cyan-400/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-400/20 transition-colors">
            <FileSearch className="w-4 h-4" /> Rapor
          </Link>
          {!v.info_locked ? (
          <button data-testid={`edit-vehicle-${v.vin}`} onClick={onEdit}
            className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-sm hover:border-cyan-400/40 hover:text-cyan-300 transition-colors">
            <Pencil className="w-4 h-4" /> Düzenle
            {v.created_at && editTimeLeft(v.created_at) && (
              <span data-testid={`edit-countdown-${v.vin}`} className="text-[10px] text-cyan-400/80 font-semibold">{editTimeLeft(v.created_at)}</span>
            )}
          </button>
          ) : (
          <span data-testid={`edit-locked-${v.vin}`}
            className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-amber-500/30 text-amber-300/90 text-xs font-semibold">
            Bilgiler Kilitli
          </span>
          )}
          <button data-testid={`transfer-vehicle-${v.vin}`} onClick={onTransfer}
            className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-sm hover:border-violet-400/40 hover:text-violet-300 transition-colors">
            <ArrowLeftRight className="w-4 h-4" /> Devret
          </button>
          {canDelete ? (
            <>
              <button data-testid={`delete-vehicle-${v.vin}`} onClick={onDelete}
                className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg border border-white/10 text-slate-300 text-sm hover:border-red-400/50 hover:text-red-300 transition-colors">
                <Trash2 className="w-4 h-4" /> Sil
              </button>
              {delRemain !== null && (
                <span data-testid={`delete-window-${v.vin}`} className="text-[10px] text-amber-300/80 text-center">Silme: {fmtHourMin(delRemain)} kaldı</span>
              )}
            </>
          ) : (
            <span data-testid={`delete-closed-${v.vin}`} className="text-[10px] text-slate-500 text-center px-2 py-1">Silme süresi doldu</span>
          )}
        </div>
      </div>

      <div className="px-5 sm:px-6 pb-4 grid grid-cols-1 sm:grid-cols-3 gap-2.5">
        {BELGE_BOXES.map((b) => {
          const t = belgeler[b.key];
          const st = belgeDurumu(t.bitis);
          return (
            <div key={b.key} data-testid={`belge-box-${b.key}-${v.vin}`}
              className="rounded-xl border border-white/10 bg-slate-950/50 px-4 py-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-slate-100">{b.label}</span>
                <span data-testid={`belge-status-${b.key}-${v.vin}`} className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
              </div>
              <p className="mt-1 text-xs text-slate-400">Başlangıç: {fmtDateShort(t.baslangic)} | Bitiş: {fmtDateShort(t.bitis)}</p>
            </div>
          );
        })}
      </div>

      <div className="border-t border-white/10 px-5 sm:px-6 py-4">
        <button data-testid={`toggle-records-${v.vin}`} onClick={onToggle}
          className="flex items-center gap-2 text-sm font-semibold text-slate-200 hover:text-cyan-300 transition-colors">
          <ClipboardList className="w-4 h-4" /> Geçmiş Kayıtları ({(v.history || []).length})
          <span className="text-xs text-slate-500">{expanded ? "▲" : "▼"}</span>
        </button>
        {expanded && (
          <div className="mt-4">
            <button data-testid={`add-record-${v.vin}`} onClick={onAddRecord}
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 transition-colors">
              <Plus className="w-4 h-4" /> Kayıt Ekle
            </button>
            <div className="mt-4 space-y-3">
              {records.map((r) => {
                const meta = EVENT_META[r.type] || EVENT_META.tescil;
                return (
                  <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-white/5 bg-slate-950/50 px-4 py-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${meta.ring} ${meta.color}`}>{meta.label}</span>
                        <span className="font-semibold text-sm text-slate-100">{r.title}</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{fmtDate(r.date)}{r.km != null && ` • ${fmtKm(r.km)}`}{r.severity && ` • ${SEVERITY_LABELS[r.severity] || r.severity}`}</p>
                      {r.description && <p className="text-sm text-slate-300 mt-1">{r.description}</p>}
                    </div>
                    {r.type !== "tescil" && (
                      <div className="flex gap-2 shrink-0">
                        <button data-testid={`edit-record-${r.id}`} onClick={() => onEditRecord(r)}
                          className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors">
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button data-testid={`delete-record-${r.id}`} onClick={() => onDeleteRecord(r.id)}
                          className="p-2 rounded-lg border border-white/10 text-slate-400 hover:text-red-300 hover:border-red-400/40 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
